import React, { useRef } from "react";
import {
  Box,
  Button,
  Heading,
  Flex,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { usePeople } from "../../hooks/usePeople";
import { UsersList } from "./UsersList";
import { AddNewSubscriber } from "./AddNewSubscriber";
import { useMutation } from "@apollo/client";
import { ADD_SUBSCRIBER, REMOVE_SUBSCRIBER } from "../../graphql/mutations";
import { CLASS_TEACHER_SUBSCRIBERS } from "../../graphql/queries";
import { AlertDialog } from "../../components/AlertDialog";

export const PeoplePage = ({ user, userRole, classId }) => {
  const {
    classTeacherStudent,
    addNewSubscriber,
    removeSubscriber,
    setIsWorking,
  } = usePeople();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: alertIsOpen,
    onOpen: alertOnOpen,
    onClose: alertOnClose,
  } = useDisclosure();
  const subscriberIdRef = useRef("");
  const toast = useToast();
  const showToast = (title, status) => {
    toast({
      position: "top",
      title,
      status,
      duration: 9000,
      isClosable: true,
    });
  };
  const [addSubscriberMutation] = useMutation(ADD_SUBSCRIBER, {
    update: (cache, { data }) => {
      cache.writeQuery({
        query: CLASS_TEACHER_SUBSCRIBERS,
        variables: {
          where: {
            id: classId,
          },
        },
        data: {
          organizationClass: {
            creator: classTeacherStudent.teachers,
            subscribers: data.updateOrganizationClass.subscribers,
          },
        },
      });
      addNewSubscriber(data.updateOrganizationClass.subscribers);
      setIsWorking(false);
      onClose();
      showToast("Successfully added.", "success");
    },
    onError: () => {
      onClose();
      setIsWorking(false);
      showToast("Failed to add. Please try again!", "error");
    },
  });
  const [removeSubscriberMutation] = useMutation(REMOVE_SUBSCRIBER, {
    update: (cache, { data }) => {
      cache.writeQuery({
        query: CLASS_TEACHER_SUBSCRIBERS,
        variables: {
          where: {
            id: classId,
          },
        },
        data: {
          organizationClass: {
            creator: classTeacherStudent.teachers,
            subscribers: classTeacherStudent.subscribers.filter(
              (s) => s.id !== data.removeSubscriberFromClass.userId
            ),
          },
        },
      });
      removeSubscriber(data.removeSubscriberFromClass.userId);
      setIsWorking(false);
      alertOnClose();
      showToast("Successfully removed.", "success");
    },
    onError: () => {
      alertOnClose();
      setIsWorking(false);
      showToast("Failed to remove. Please try again!", "error");
    },
  });
  const deleteSubscriber = () => {
    setIsWorking(true);
    removeSubscriberMutation({
      variables: {
        where: {
          classId: classId,
          userId: subscriberIdRef.current,
        },
      },
    });
  };
  const addSubscribers = (subscribers) => {
    setIsWorking(true);
    addSubscriberMutation({
      variables: {
        where: {
          id: classId,
        },
        data: {
          subscribers: {
            connect: subscribers.map((s) => ({ id: s.id })),
          },
        },
      },
    });
  };
  return (
    <Box
      style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "30px 20px",
      }}
    >
      <Flex
        style={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Heading
          as="h1"
          style={{
            fontSize: "24px",
            color: "rgb(66, 71, 112)",
          }}
        >
          People
        </Heading>
        {userRole !== "Student" && (
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            variant="solid"
            onClick={onOpen}
          >
            {userRole === "Teacher" ? "Add student" : "Add child"}
          </Button>
        )}
      </Flex>
      <UsersList
        title="Teachers"
        usersList={classTeacherStudent?.teachers}
        isTeacher={true}
      />
      <UsersList
        title={userRole === "Teacher" ? "Students" : "Childs"}
        usersList={classTeacherStudent?.subscribers}
        isTeacher={false}
        isRemoveButton={userRole !== "Student"}
        removeSubscriber={(id) => {
          subscriberIdRef.current = id;
          alertOnOpen();
        }}
      />
      <AddNewSubscriber
        user={user}
        userRole={userRole}
        isOpen={isOpen}
        onClose={onClose}
        existingUsers={classTeacherStudent?.subscribers?.map((s) => s.id)}
        onAdd={addSubscribers}
        isworking={classTeacherStudent.isWorking}
      />
      <AlertDialog
        title={userRole === "Teacher" ? "Delete student" : "Delete child"}
        description="Are you sure? You can't undo this action afterwards. Deleting it will remove all the submissions and reports from the class."
        actionButtonText="Delete"
        isOpen={alertIsOpen}
        onCancel={alertOnClose}
        onDelete={deleteSubscriber}
        isWorking={classTeacherStudent.isWorking}
        loadingText="Deleting"
      />
    </Box>
  );
};
